import { useState, useMemo, useEffect, useCallback } from "react";
import { Button, Modal, Input, Avatar, Checkbox } from "antd";
import { useUser } from "@/contexts/UserContext";
import { TableState } from "@/types/tableState.types";
import { Workspace } from "@/components/UI/TableToolbar/WorkspaceButton/workspace";
import { useSuperuserMode } from "@/contexts/useSuperuser";

import { formatDateText } from "@/utils/dateUtils";
import { getInitials } from "@/utils/avatarUtils";

const maxCharacters = 50;

type ErrorType = "empty" | "duplicate" | null;

const errorMessages: Record<Exclude<ErrorType, null>, string> = {
  empty: "Please enter a name",
  duplicate: "Name already in use",
};

type WorkspaceModalProps = {
  isOpen: boolean;
  onCancel?: () => void;
  onConfirm?: (name: string, isVivoTemplate?: boolean) => void | Promise<void>;
  newConfiguration?: TableState;
  workspace?: Workspace;
  defaultValue?: string;
  existingNames?: string[];
};

export function WorkspaceModal({
  isOpen,
  onCancel,
  onConfirm,
  workspace,
  defaultValue,
  existingNames,
}: WorkspaceModalProps) {
  const [name, setName] = useState(
    (defaultValue || workspace?.title || "New Workspace").slice(
      0,
      maxCharacters,
    ),
  );
  const [errorType, setErrorType] = useState<ErrorType>(null);
  const { userInfo } = useUser();
  const [isVivoTemplate, setIsVivoTemplate] = useState(false);
  const { isSuperuserMode } = useSuperuserMode();

  const validateName = useCallback(
    (value: string): ErrorType => {
      if (!value.trim()) return "empty";
      if (
        existingNames?.some(
          (existingName) =>
            existingName.toLowerCase() === value.trim().toLowerCase() &&
            // Allow keeping same name when editing
            !(workspace?.title?.toLowerCase() === value.trim().toLowerCase()),
        )
      ) {
        return "duplicate";
      }
      return null;
    },
    [existingNames, workspace?.title],
  );

  useEffect(() => {
    if (isOpen) {
      const initialName = (
        defaultValue ||
        workspace?.title ||
        "New Workspace"
      ).slice(0, maxCharacters);
      setName(initialName);
      setErrorType(null);
      setIsVivoTemplate(workspace?.is_vivo_template || false);
    }
  }, [isOpen, defaultValue, workspace]);

  const dateCreated = useMemo(() => {
    if (workspace?.created_at) {
      return formatDateText(workspace?.created_at);
    }
    return formatDateText(new Date().toISOString());
  }, [workspace?.created_at]);

  const dateModified = useMemo(() => {
    if (workspace?.modified_at) {
      return formatDateText(workspace?.modified_at);
    }
  }, [workspace?.modified_at]);

  const creator =
    workspace?.created_by || userInfo?.first_name
      ? (userInfo?.first_name ?? "") + " " + (userInfo?.last_name ?? "")
      : "Unknown User";
  const authorInitials = useMemo(() => {
    return getInitials(creator);
  }, [creator]);

  const handleCancel = () => {
    onCancel?.();
  };

  const handleNameChange = (value: string) => {
    setName(value);
    if (errorType) {
      setErrorType(null);
    }
  };

  const handleBlur = () => {
    setErrorType(validateName(name));
  };

  const handleConfirm = async () => {
    try {
      const error = validateName(name);
      if (error) {
        setErrorType(error);
        return;
      }
      if (onConfirm) {
        await onConfirm(name, isSuperuserMode ? isVivoTemplate : undefined);
      }
      onCancel?.();
    } catch (error) {
      console.error("Error confirming workspace:", error);
    }
  };

  return (
    <Modal
      destroyOnHidden={true}
      open={isOpen}
      footer={
        <div className="flex items-center justify-between">
          <Button
            data-testid="workspace-modal-cancel-button"
            onClick={handleCancel}
            className="bg-white text-[#064C68] border-[#064C68] border hover:border-[#064C68]"
          >
            Cancel
          </Button>
          <Button
            data-testid="workspace-modal-save-button"
            disabled={!name.trim() || errorType !== null}
            onClick={handleConfirm}
            className="bg-[#0C98D0] text-white hover:bg-[#064C68]! hover:text-white! border-[#0C98D0] border hover:border-[#064C68]!"
          >
            Save
          </Button>
        </div>
      }
      closable={true}
      onCancel={handleCancel}
      centered
      title={
        <div className="text-lg font-bold text-[#2B4752] border-b border-[#F0F0F0] pb-2">
          Workspace Settings
        </div>
      }
      width={450}
    >
      <div data-testid="workspace-modal" className="space-y-2 mb-10 mt-4">
        <div>
          <label className="block text-sm font-bold text-[#2B4752] mb-2">
            Workspace Name
          </label>
          <Input
            data-testid="workspace-name-input"
            value={name}
            onChange={(e) => handleNameChange(e.target.value)}
            onBlur={handleBlur}
            onPressEnter={handleConfirm}
            autoFocus
            status={errorType ? "error" : ""}
            className={`w-full rounded-lg p-2 ${
              errorType ? "bg-[#ffd8d880]! border! border-[#980000]!" : ""
            }`}
            maxLength={maxCharacters}
            showCount
          />
          {errorType && (
            <p className="text-[#980000] text-xs mt-1">
              {errorMessages[errorType]}
            </p>
          )}
        </div>

        {isSuperuserMode && (
          <div className="mt-4">
            <Checkbox
              checked={isVivoTemplate}
              onChange={(e) => setIsVivoTemplate(e.target.checked)}
            >
              <span className="text-sm text-[#2B4752]">Is Vivo Template</span>
            </Checkbox>
          </div>
        )}

        <table>
          <tbody>
            <tr>
              <td className="text-xs text-[#8C8C8C] uppercase tracking-wide py-4 pr-4">
                Date Created
              </td>
              <td
                data-testid="workspace-modal-date-created"
                className="text-sm text-[#2B4752]"
              >
                {dateCreated}
              </td>
            </tr>
            {workspace?.modified_at && (
              <tr>
                <td className="text-xs text-[#8C8C8C] uppercase tracking-wide py-4 pr-4">
                  Date Modified
                </td>
                <td
                  data-testid="workspace-modal-date-modified"
                  className="text-sm text-[#2B4752]"
                >
                  {dateModified}
                </td>
              </tr>
            )}
            <tr>
              <td className="text-xs text-[#8C8C8C] uppercase tracking-wide py-4 pr-4">
                Author
              </td>
              <td
                data-testid="workspace-modal-author"
                className="text-sm text-[#2B4752]"
              >
                <div className="flex items-center gap-2">
                  <Avatar size={28} className="bg-[#4A9CB8] text-white text-xs">
                    {authorInitials}
                  </Avatar>{" "}
                  <span className="text-sm text-[#2B4752]">{creator}</span>{" "}
                </div>{" "}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </Modal>
  );
}
